import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import {
  SERVICE_DIVISIONS,
  getServicesByDivision,
} from "../config/services";
import { getStatusMeta } from "../config/statuses";
import { resolveIcon } from "../config/icons";
import StatusBadge from "../components/product/StatusBadge";
import Footer from "../components/Footer";

export default function ServicesPage() {
  const [activeDivision, setActiveDivision] = useState(SERVICE_DIVISIONS[0]?.id);

  const services = useMemo(
    () => getServicesByDivision(activeDivision),
    [activeDivision]
  );

  const activeMeta = SERVICE_DIVISIONS.find((d) => d.id === activeDivision);

  return (
    <div className="min-h-screen pt-28" data-testid="services-page">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 pb-24">
        <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-[color:var(--gold)] mb-3">
          § Engineering &amp; Technology Services
        </div>
        <h1 className="display display-hero text-4xl md:text-6xl tracking-[-0.03em] max-w-4xl">
          Four divisions. One engineering standard.
        </h1>
        <p className="mt-5 text-base md:text-lg text-steel-text leading-relaxed max-w-3xl">
          Industrial automation, electrical engineering, electronics engineering and
          digital platforms — quoted through DEBSUN Technologies. Large-scale EPC
          execution is handled separately through our strategic engineering partner.
        </p>

        <div className="mt-12 flex flex-wrap gap-2" data-testid="services-division-tabs">
          {SERVICE_DIVISIONS.map((d) => {
            const on = d.id === activeDivision;
            return (
              <button
                key={d.id}
                onClick={() => {
                  setActiveDivision(d.id);
                  const el = document.getElementById(d.id);
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className={`font-mono-2 text-[11px] tracking-[0.18em] uppercase px-4 py-2.5 border transition-colors ${
                  on
                    ? "border-[color:var(--gold)] text-foreground"
                    : "border-border text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`services-tab-${d.id}`}
              >
                {d.name}
              </button>
            );
          })}
        </div>

        {SERVICE_DIVISIONS.map((division) => {
          const divisionServices = getServicesByDivision(division.id);
          const Icon = resolveIcon(division.icon);
          const status = getStatusMeta(division.status);
          return (
            <section
              key={division.id}
              id={division.id}
              className="mt-20 scroll-mt-28"
              data-testid={`services-section-${division.id}`}
            >
              <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
                <div className="flex items-start gap-5">
                  <div className="w-14 h-14 border border-border flex items-center justify-center shrink-0">
                    <Icon size={26} strokeWidth={1.3} className="text-[color:var(--gold)]" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-mono-2 text-[10px] tracking-[0.3em] text-muted-foreground">
                        {division.code}
                      </span>
                      <StatusBadge status={division.status} />
                    </div>
                    <h2 className="display display-section text-3xl md:text-4xl">
                      {division.name}
                    </h2>
                    <p className="mt-3 text-sm text-steel-text leading-relaxed max-w-2xl">
                      {division.description}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {divisionServices.map((service, i) => {
                  const SIcon = resolveIcon(service.icon);
                  const sStatus = getStatusMeta(service.status);
                  return (
                    <motion.div
                      key={service.id}
                      initial={{ opacity: 0, y: 24 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.55, delay: (i % 3) * 0.06 }}
                      className="steel-card clip-frame p-7 flex flex-col min-h-[300px]"
                      data-testid={`service-card-${service.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <SIcon
                          size={24}
                          strokeWidth={1.3}
                          className="text-[color:var(--steel)]"
                        />
                        <span
                          className="font-mono-2 text-[10px] tracking-[0.18em] uppercase"
                          style={{ color: sStatus.color }}
                        >
                          {sStatus.label}
                        </span>
                      </div>
                      <h3 className="display display-section text-xl mt-6">
                        {service.name}
                      </h3>
                      <p className="mt-3 text-sm text-steel-text leading-relaxed flex-1">
                        {service.description}
                      </p>

                      <div className="mt-5">
                        <div className="font-mono-2 text-[9px] tracking-[0.2em] uppercase text-muted-foreground mb-2">
                          Applications
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {service.applications.map((a) => (
                            <span
                              key={a}
                              className="font-mono-2 text-[9px] tracking-[0.12em] uppercase px-2 py-1 border border-border text-muted-foreground"
                            >
                              {a}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="mt-4">
                        <div className="font-mono-2 text-[9px] tracking-[0.2em] uppercase text-muted-foreground mb-2">
                          Industries Served
                        </div>
                        <p className="text-xs text-steel-text leading-relaxed">
                          {service.industries.join(" · ")}
                        </p>
                      </div>

                      <div className="mt-7 pt-5 border-t border-border">
                        {service.quoteEnabled ? (
                          <Link
                            to={`/portal?service=${encodeURIComponent(service.id)}`}
                            className="inline-flex items-center gap-2 font-mono-2 text-[11px] tracking-[0.2em] uppercase"
                            style={{ color: "var(--gold)" }}
                            data-testid={`service-quote-${service.id}`}
                          >
                            Request Quote <ArrowUpRight size={14} />
                          </Link>
                        ) : service.href ? (
                          <Link
                            to={service.href}
                            className="inline-flex items-center gap-2 font-mono-2 text-[11px] tracking-[0.2em] uppercase"
                            style={{ color: "var(--gold)" }}
                          >
                            Learn More <ArrowUpRight size={14} />
                          </Link>
                        ) : (
                          <span className="font-mono-2 text-[11px] tracking-[0.2em] uppercase text-muted-foreground">
                            Coming Soon
                          </span>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </section>
          );
        })}

        <div className="mt-20 border border-border bg-secondary/40 p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="font-mono-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-2">
              Need a different scope?
            </div>
            <p className="text-sm text-steel-text leading-relaxed max-w-xl">
              {activeMeta
                ? `Discuss ${activeMeta.name.toLowerCase()} requirements with our engineering team.`
                : "Discuss requirements with our engineering team."}
            </p>
          </div>
          <Link
            to="/#contact"
            className="btn-gold clip-frame-sm px-6 py-4 text-[11px] uppercase tracking-widest font-mono-2 inline-flex items-center gap-2"
          >
            Contact Engineering <ArrowUpRight size={14} />
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
